import { useNavigate } from 'react-router-dom';
import { useForm } from '../../hooks/useForm';
import { FormField, FormSelect, FormTextArea, SmartFormActions } from '../shared/form';
import { McpConnection } from '../../types';

export interface McpConnectionFormData {
  name: string;
  url: string;
  description: string;
  isActive: boolean;
  authType: 'none' | 'bearer' | 'api-key' | 'basic';
  authHeader: string;
  credential: string;
}

interface McpConnectionFormProps {
  /**
   * `authHeader` is widened in here rather than onto `McpConnection` (task 962):
   * the create page needs to carry an Assistant-supplied header name into the
   * form, and the entity type is the shape of a LIST row, which does not carry
   * one. Renaming or reshaping the entity type is not this form's business.
   */
  initialData?: Partial<McpConnection & { hasCredential?: boolean; authHeader?: string }>;
  onSubmit: (data: any) => Promise<void>;
  onCancel?: () => void;
  mode?: 'create' | 'edit';
  /**
   * Put the cursor in the credential field on mount (task 962).
   *
   * Set by the create page when the Assistant pre-filled everything else: the
   * key is then the only thing left to supply, and it is the one thing the
   * Assistant can never supply for them.
   */
  focusCredential?: boolean;
}

const AUTH_TYPE_OPTIONS = [
  { value: 'none', label: 'None' },
  { value: 'bearer', label: 'Bearer Token' },
  { value: 'api-key', label: 'API Key' },
  { value: 'basic', label: 'Basic Auth' },
];

export function McpConnectionForm({
  initialData,
  onSubmit,
  onCancel,
  mode = 'create',
  focusCredential = false,
}: McpConnectionFormProps) {
  const navigate = useNavigate();

  const {
    values,
    errors,
    isSubmitting,
    isDirty,
    isValid,
    handleChange,
    setFieldValue,
    handleSubmit,
  } = useForm<McpConnectionFormData>({
    initialValues: {
      name: initialData?.name || '',
      url: initialData?.url || '',
      description: initialData?.description || '',
      isActive: initialData?.isActive ?? true,
      authType: initialData?.authType || 'none',
      // Carried from `initialData` since task 962, so an Assistant pre-fill can
      // supply the vendor's header name. It is not a secret — the credential
      // below is, and that one is never pre-filled from anywhere.
      authHeader: initialData?.authHeader || '',
      credential: '',
    },
    requiredFields: mode === 'create' ? ['name', 'url'] : ['url'],
    onSubmit: async (formValues) => {
      const data: any = {
        url: formValues.url.trim(),
        isActive: formValues.isActive,
        authType: formValues.authType,
      };

      // Only include name in create mode (edit mode handles name separately in header)
      if (mode === 'create') {
        data.name = formValues.name.trim();
      }

      if (formValues.description.trim()) {
        data.description = formValues.description.trim();
      }

      if (formValues.authType === 'api-key' && formValues.authHeader.trim()) {
        data.authHeader = formValues.authHeader.trim();
      }

      // Only include credential if provided (for create it's required for non-none auth, for edit it's optional)
      if (formValues.credential) {
        data.credential = formValues.credential;
      }

      await onSubmit(data);
    },
    validate: (values) => {
      const errors: Partial<Record<keyof McpConnectionFormData, string>> = {};

      // Only validate name in create mode (edit mode handles name separately in header)
      if (mode === 'create') {
        if (!values.name.trim()) {
          errors.name = 'Name is required';
        } else if (values.name.length > 100) {
          errors.name = 'Name must be 100 characters or less';
        }
      }

      if (!values.url.trim()) {
        errors.url = 'URL is required';
      } else {
        try {
          new URL(values.url);
        } catch {
          errors.url = 'Please enter a valid URL';
        }
      }

      if (values.description && values.description.length > 500) {
        errors.description = 'Description must be 500 characters or less';
      }

      if (values.authType === 'api-key' && !values.authHeader.trim()) {
        errors.authHeader = 'Header name is required for API Key auth';
      }

      // For create mode, credential is required if auth type is not 'none'
      if (mode === 'create' && values.authType !== 'none' && !values.credential) {
        errors.credential = 'Credential is required';
      }

      return errors;
    },
  });

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    } else {
      navigate('/mcp-connections');
    }
  };

  const handleAuthTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newAuthType = e.target.value as McpConnectionFormData['authType'];
    setFieldValue('authType', newAuthType);
    // Clear auth-related fields when changing type
    if (newAuthType === 'none') {
      setFieldValue('authHeader', '');
      setFieldValue('credential', '');
    }
  };

  const needsCredential = values.authType !== 'none';
  const needsAuthHeader = values.authType === 'api-key';

  const getCredentialLabel = () => {
    switch (values.authType) {
      case 'bearer':
        return 'Bearer Token';
      case 'api-key':
        return 'API Key Value';
      case 'basic':
        return 'Credentials (base64 encoded)';
      default:
        return 'Credential';
    }
  };

  const getCredentialHelp = () => {
    switch (values.authType) {
      case 'bearer':
        return 'The bearer token to use for authentication';
      case 'api-key':
        return 'The API key value to send in the specified header';
      case 'basic':
        return 'Base64-encoded username:password string';
      default:
        return '';
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div className="space-y-6">
          {mode === 'create' && (
            <FormField
              label="Connection Name"
              name="name"
              type="text"
              value={values.name}
              onChange={handleChange}
              error={errors.name}
              placeholder="e.g., GitHub MCP Server"
              helpText="A unique name to identify this MCP connection"
              required
            />
          )}

          <FormField
            label="URL"
            name="url"
            type="text"
            value={values.url}
            onChange={handleChange}
            error={errors.url}
            placeholder="https://mcp.example.com"
            helpText="The MCP server endpoint URL"
            required
          />

          <FormTextArea
            label="Description"
            name="description"
            value={values.description}
            onChange={handleChange}
            error={errors.description}
            placeholder="Describe what this MCP connection provides..."
            helpText="Optional description of the connection's purpose"
            rows={3}
          />

          <div className="flex items-center space-x-3">
            <input
              type="checkbox"
              id="isActive"
              name="isActive"
              checked={values.isActive}
              onChange={(e) => setFieldValue('isActive', e.target.checked)}
              className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
            />
            <label htmlFor="isActive" className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Active
            </label>
            <span className="text-sm text-gray-500 dark:text-gray-400">
              (Inactive connections won't be available to agents)
            </span>
          </div>
        </div>

        <div className="space-y-6">
          <FormSelect
            label="Authentication Type"
            name="authType"
            value={values.authType}
            onChange={handleAuthTypeChange}
            options={AUTH_TYPE_OPTIONS}
            error={errors.authType}
            helpText="How to authenticate with this MCP server"
          />

          {needsAuthHeader && (
            <FormField
              label="Header Name"
              name="authHeader"
              type="text"
              value={values.authHeader}
              onChange={handleChange}
              error={errors.authHeader}
              placeholder="e.g., X-API-Key"
              helpText="The HTTP header name to send the API key in"
              required
            />
          )}

          {needsCredential && (
            <FormField
              label={getCredentialLabel()}
              name="credential"
              type="password"
              value={values.credential}
              onChange={handleChange}
              error={errors.credential}
              autoFocus={focusCredential}
              placeholder={mode === 'edit' ? 'Leave blank to keep existing' : ''}
              helpText={
                mode === 'edit'
                  ? `${getCredentialHelp()}. Leave blank to keep the existing credential.`
                  : getCredentialHelp()
              }
              required={mode === 'create'}
            />
          )}

          <div className="bg-gray-50 dark:bg-gray-900 p-4 rounded-lg">
            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Preview</h4>
            <dl className="space-y-2 text-sm">
              {mode === 'create' && (
                <div className="flex justify-between">
                  <dt className="text-gray-500 dark:text-gray-400">Name</dt>
                  <dd className="text-gray-900 dark:text-white font-medium truncate max-w-[200px]">
                    {values.name || '-'}
                  </dd>
                </div>
              )}
              <div className="flex justify-between">
                <dt className="text-gray-500 dark:text-gray-400">URL</dt>
                <dd className="text-gray-900 dark:text-white font-mono text-xs truncate max-w-[200px]">
                  {values.url || '-'}
                </dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-500 dark:text-gray-400">Auth Type</dt>
                <dd className="text-gray-900 dark:text-white">
                  {AUTH_TYPE_OPTIONS.find((o) => o.value === values.authType)?.label}
                </dd>
              </div>
              {needsAuthHeader && (
                <div className="flex justify-between">
                  <dt className="text-gray-500 dark:text-gray-400">Header</dt>
                  <dd className="text-gray-900 dark:text-white font-mono text-xs">
                    {values.authHeader || '-'}
                  </dd>
                </div>
              )}
              <div className="flex justify-between">
                <dt className="text-gray-500 dark:text-gray-400">Status</dt>
                <dd>
                  <span
                    className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                      values.isActive
                        ? 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200'
                    }`}
                  >
                    {values.isActive ? 'Active' : 'Inactive'}
                  </span>
                </dd>
              </div>
            </dl>
          </div>
        </div>
      </div>

      <SmartFormActions
        mode={mode}
        isDirty={isDirty}
        isValid={isValid}
        isSubmitting={isSubmitting}
        onCancel={handleCancel}
        submitLabel={mode === 'create' ? 'Create Connection' : 'Save Changes'}
      />
    </form>
  );
}
